
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "@helpers/api.js";
import Parser from "@helpers/parser.js";
import { getIntlLocale } from "@i18n";

import { Table, Text, InfoBlock, StatusChip } from "@fragments";
import MetricCard from "@pages/Banking/components/MetricCard";

import "./styles.scss";

export default function FundsProjection() {
    const { t } = useTranslation("banking");
    const [loading, setLoading] = useState(true);
    const [projection, setProjection] = useState([]);
    const [summary, setSummary] = useState({ future: 0, paid: 0 });

    useEffect(() => {
        handleLoad();
    }, []);

    async function handleLoad() {
        setLoading(true);
        try {
            const { data } = await api.get("funds/projection?weeks=12");
            if (data) {
                if (data.raw) setProjection(data.raw);
                setSummary({
                    future: data.future || 0,
                    paid: data.paid || 0
                });
            }
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    }

    const columns = [
        {
            id: 'amount',
            title: t('common:fields.value'),
            grow: 1,
            customRender: (data) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Text.h5 contrast={7} weight={500}>
                        R$ {Parser.price.brl(data.amount)}
                    </Text.h5>
                    <Text.span contrast={5} weight={400}>BRL</Text.span>
                </div>
            )
        },
        {
            id: 'date',
            title: t('withdrawals.projection.availabilityDate'),
            grow: 1,
            customRender: (data) => {
                const [year, month, day] = data.date.split('-');
                const date = new Date(year, month - 1, day);
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const diffTime = date - today;
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                const daysLabel = diffDays === 0
                    ? t('withdrawals.projection.today')
                    : diffDays === 1
                        ? t('withdrawals.projection.tomorrow')
                        : t('withdrawals.projection.inDays', { count: diffDays });

                return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                        <StatusChip.Default text={daysLabel} />
                        <Text.span contrast={7} weight={400}>
                            {date.toLocaleDateString(getIntlLocale(), { day: 'numeric', month: 'short', year: 'numeric' })}
                        </Text.span>
                    </div>
                );
            }
        }
    ];

    if (projection.length === 0 && !loading && summary.future === 0 && summary.paid === 0) {
        return null;
    }

    return (
        <div className="funds-projection mt-5">
            <Text.h3 weight={500} className="mb-2">
                {t('withdrawals.projection.title')}
            </Text.h3>

            <InfoBlock
                title={t('withdrawals.projection.infoTitle')}
                description={t('withdrawals.projection.infoDescription')}
                className="mb-2"
            />

            <div className="metrics-row mb-4" style={{ display: 'flex', gap: '16px' }}>
                <MetricCard
                    title={t('withdrawals.projection.receivable')}
                    metric={`R$ ${Parser.price.brl(summary.future)}`}
                    loading={loading}
                />
                <MetricCard
                    title={t('withdrawals.projection.released')}
                    metric={`R$ ${Parser.price.brl(summary.paid)}`}
                    loading={loading}
                />
            </div>

            <Text.h4 weight={500} className="">
                {t('withdrawals.projection.nextWeeks')}
            </Text.h4>

            <Table
                className="mt-2"
                id="banking-funds-projection-table"
                columns={columns}
                data={projection}
                loading={loading}
                loadingRows={4}
                contained={true}
                emptyMessage={t('withdrawals.projection.empty')}
                enablePagination={false}
            />
        </div>
    );
}
